import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { msmartAxios } from "../../../api/axios";
import { useAuthContext } from "../../../hooks/useAuthContext";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../utils/errorMessage";

// =========================
// Helpers
// =========================
function toYMD(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDT(dt) {
  try {
    return new Date(dt).toLocaleString("en-GB", {
      day: "2-digit",
      month: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "-";
  }
}

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}

function getMedal(rank, totalPoints) {
  if (!totalPoints || totalPoints <= 0) return "";
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return "";
}

function pointsBadgeClass(p) {
  if (p >= 15) return "bg-dark";
  if (p >= 8) return "bg-primary";
  if (p >= 3) return "bg-secondary";
  return "bg-light text-dark";
}

function SectionCard({ title, right, children }) {
  return (
    <div className="card p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fw-bold">{title}</div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function SkeletonTable({ rows = 7 }) {
  return (
    <div className="table-responsive mt-3">
      <table className="table table-bordered table-sm align-middle">
        <thead className="table-light">
          <tr>
            <th style={{ width: 80 }}>Rank</th>
            <th>Name</th>
            <th style={{ width: 140 }}>Added</th>
            <th style={{ width: 120 }}>Closed</th>
            <th style={{ width: 120 }}>Booked</th>
            <th style={{ width: 120 }}>Rejected</th>
            <th style={{ width: 120 }} title="Unique leads worked on per day">Leads Touched</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i}>
              <td>
                <div className="ss-skeleton" style={{ height: 14, borderRadius: 8, width: 60 }} />
              </td>
              <td>
                <div className="ss-skeleton" style={{ height: 14, borderRadius: 8, width: "55%" }} />
                <div className="ss-skeleton mt-2" style={{ height: 12, borderRadius: 8, width: "25%" }} />
              </td>
              {Array.from({ length: 5 }).map((__, k) => (
                <td key={k}>
                  <div className="ss-skeleton" style={{ height: 14, borderRadius: 8, width: 40, margin: "0 auto" }} />
                </td>
              ))}
              <td>
                <div className="ss-skeleton" style={{ height: 14, borderRadius: 8, width: 40, margin: "0 auto" }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =========================
// Component
// =========================
export default function TeamActivity() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const { notifyError } = useToast();

  // Default start: today
  const today = useMemo(() => new Date(), []);
  const [dateRange, setDateRange] = useState(() => {
    const ymd = toYMD(new Date());
    return { startDate: ymd, endDate: ymd };
  });

  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI
  const [q, setQ] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Modal
  const [modalData, setModalData] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [showModal, setShowModal] = useState(false);

  const fetchActivityData = useCallback(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    setLoading(true);
    try {
      const res = await msmartAxios.post(
        `/api/msmart/manager/get/activity/${teamId}`,
        { startDate: dateRange.startDate, endDate: dateRange.endDate },
        { headers: { accessToken: user.token } }
      );

      const rows = (res?.data || [])
        .map((item) => {
          const totalPoints =
            (Number(item.totalClosed) || 0) * 3 +
            (Number(item.totalBooking) || 0) * 1.5 +
            (Number(item.totalCreated) || 0) * 1 +
            (Number(item.totalFollowUp) || 0) * 1 +
            (Number(item.totalRejected) || 0) * 0.25;

          return { ...item, totalPoints };
        })
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .map((item, index) => ({ ...item, rank: index + 1 }));

      setActivityData(rows);
      setCurrentPage(1);
    } catch (err) {
      notifyError(getErrorMessage(err, "Failed to fetch activity data."));
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, teamId, user.token, notifyError]);

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  // Derived filtered rows
  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    if (!text) return activityData;

    return activityData.filter((r) => {
      const hay = `${r.nameInTeam || ""} ${r.username || ""}`.toLowerCase();
      return hay.includes(text);
    });
  }, [q, activityData]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  const currentItems = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filtered.slice(start, end);
  }, [filtered, safePage]);

  useEffect(() => {
    // If filter shrinks, ensure page valid
    setCurrentPage(1);
  }, [q, dateRange.startDate, dateRange.endDate]);

  const handlePageChange = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

  const handleViewDetails = (data, category, name) => {
    const safe = Array.isArray(data) ? data : [];
    setModalData(safe);
    setModalTitle(`${name} — ${category}`);
    setShowModal(true);
  };

  const handleDateChange = (selectedDates) => {
    // range mode: [start, end]
    if (selectedDates?.length === 2) {
      const [start, end] = selectedDates;
      setDateRange({
        startDate: toYMD(start),
        endDate: toYMD(end),
      });
    }
  };

  // KPI summary (top bar)
  const kpi = useMemo(() => {
    const totals = filtered.reduce(
      (acc, r) => {
        acc.created += Number(r.totalCreated) || 0;
        acc.closed += Number(r.totalClosed) || 0;
        acc.booking += Number(r.totalBooking) || 0;
        acc.rejected += Number(r.totalRejected) || 0;
        acc.followup += Number(r.totalFollowUp) || 0;
        acc.points += Number(r.totalPoints) || 0;
        return acc;
      },
      { created: 0, closed: 0, booking: 0, rejected: 0, followup: 0, points: 0 }
    );
    return totals;
  }, [filtered]);

  return (
    <div className="ss-page">
      <div className="container py-6">
        {/* Header */}
        <div className="row justify-content-center text-start">
          <div className="col-lg-12">
            <h1 className="header-title mb-1">M-Smart</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
            Sales Activity Management.
            </div>
          </div>
        </div>

        {/* Segment (Team / Personal) */}
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="msmart-segment my-4">
              <NavLink to="#" className={() => "msmart-seg-link active"}>
                Team
              </NavLink>
              <NavLink
                to={`/msmart/manager/database/${teamId}`}
                className={({ isActive }) => `msmart-seg-link ${isActive ? "active" : ""}`}
              >
                Personal
              </NavLink>
            </div>
          </div>
        </div>

        {/* Main card */}
        <div className="card text-center mt-4">
          {/* Tabs */}
          <div className="card-header text-start">
            <ul className="nav nav-tabs card-header-tabs flex-wrap">
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/summary/${teamId}`}>
                  Summary
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/summary/${teamId}?view=analysis`}>Analysis</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/manage/${teamId}`}>
                  Manage
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link active" to="#">
                  Activities
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/followup/${teamId}`}>
                  Follow Up
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={`/msmart/team/leads/${teamId}`}>
                  Leads
                </Link>
              </li>
            </ul>
          </div>

          <div className="card-body text-start">
            {/* Toolbar */}
            <SectionCard
              title="Filters"
              right={
                <span className="badge bg-light text-dark">
                  {loading ? "Loading..." : `${filtered.length} member(s)`}
                </span>
              }
            >
              <div className="row g-2 align-items-end">
                {/* Date range */}
                <div className="col-12 col-lg-5">
                  <label className="form-label mb-1">Date Range</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <i className="bi bi-calendar3" />
                    </span>

                    <Flatpickr
                      className="form-control"
                      options={{
                        mode: "range",
                        dateFormat: "d-m-Y",
                        allowInput: true,
                        disableMobile: true,
                        defaultDate: [new Date(dateRange.startDate), new Date(dateRange.endDate)],
                      }}
                      value={[
                        new Date(dateRange.startDate),
                        new Date(dateRange.endDate),
                      ]}
                      onChange={handleDateChange}
                    />

                    <button
                      className="btn btn-outline-dark"
                      type="button"
                      onClick={() => {
                        const ymd = toYMD(today);
                        setDateRange({ startDate: ymd, endDate: ymd });
                      }}
                      disabled={loading}
                      title="Reset to today"
                    >
                      Today
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="col-12 col-lg-4">
                  <label className="form-label mb-1">Search</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <i className="bi bi-search" />
                    </span>
                    <input
                      className="form-control"
                      placeholder="Search name / username..."
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                    {q ? (
                      <button className="btn btn-outline-dark" type="button" onClick={() => setQ("")}>
                        Clear
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Actions */}
                <div className="col-12 col-lg-3 d-flex gap-2 justify-content-lg-end">
                  <button
                    className="btn btn-outline-dark w-100"
                    type="button"
                    onClick={fetchActivityData}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-repeat me-2" />
                    Refresh
                  </button>
                </div>
              </div>
            </SectionCard>

            {/* KPI chips */}
            <div className="d-flex flex-wrap gap-2 mt-3">
              <span className="ds-chip">
                <i className="bi bi-plus-circle" /> Added: <b>{kpi.created}</b>
              </span>
              <span className="ds-chip">
                <i className="bi bi-check2-circle" /> Closed: <b>{kpi.closed}</b>
              </span>
              <span className="ds-chip">
                <i className="bi bi-calendar-check" /> Booked: <b>{kpi.booking}</b>
              </span>
              <span className="ds-chip">
                <i className="bi bi-x-circle" /> Rejected: <b>{kpi.rejected}</b>
              </span>
              <span className="ds-chip">
                <i className="bi bi-arrow-repeat" /> Follow Up: <b>{kpi.followup}</b>
              </span>
              <span className={`ds-chip ${kpi.points ? "ds-chip-dark" : ""}`}>
                <i className="bi bi-lightning-charge" /> Points: <b>{kpi.points.toFixed(2)}</b>
              </span>
            </div>

            {/* Table */}
            {loading ? (
              <SkeletonTable rows={8} />
            ) : (
              <>
                {/* Pagination top */}
                <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                  <button
                    className="btn btn-light"
                    disabled={safePage === 1}
                    onClick={() => handlePageChange(safePage - 1)}
                  >
                    <i className="bi bi-caret-left" />
                  </button>

                  <span className="text-muted" style={{ fontSize: 13 }}>
                    Page <b>{safePage}</b> of <b>{totalPages}</b>
                  </span>

                  <button
                    className="btn btn-light"
                    disabled={safePage === totalPages}
                    onClick={() => handlePageChange(safePage + 1)}
                  >
                    <i className="bi bi-caret-right" />
                  </button>
                </div>

                <div className="table-responsive mt-2">
                  <table className="table table-bordered table-hover text-center align-middle ss-table" style={{ fontSize: "0.9rem" }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 90 }}>Rank</th>
                        <th className="text-start">Name</th>
                        <th style={{ width: 150 }}>Added</th>
                        <th style={{ width: 120 }}>Closed</th>
                        <th style={{ width: 120 }}>Booked</th>
                        <th style={{ width: 120 }}>Rejected</th>
                        <th style={{ width: 140 }} title="Unique leads worked on per day">Leads Touched</th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentItems.length ? (
                        currentItems.map((data) => (
                          <tr key={data.username}>
                            <td className="fw-semibold">
                              {getMedal(data.rank, data.totalPoints)} {data.rank}
                              <div className="mt-1">
                                <span className={`badge ${pointsBadgeClass(data.totalPoints)} ss-badge`}>
                                  {Number(data.totalPoints || 0).toFixed(2)} pts
                                </span>
                              </div>
                            </td>

                            <td className="text-start">
                              <div className="d-flex align-items-center gap-2">
                                <div>
                                  <div className="fw-semibold">{data.nameInTeam}</div>
                                  <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                                    @{data.username}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td>
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <span className="fw-semibold">{data.totalCreated}</span>
                                <button
                                  className="btn btn-light btn-sm"
                                  onClick={() => handleViewDetails(data.createdLeads, "Added Database", data.nameInTeam)}
                                  title="View details"
                                >
                                  <i className="bi bi-eye" />
                                </button>
                              </div>
                            </td>

                            <td>
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <span className="fw-semibold">{data.totalClosed}</span>
                                {data.totalRepeat > 0 && <small className="text-muted ms-1">+{data.totalRepeat} repeat</small>}
                                <button
                                  className="btn btn-light btn-sm"
                                  onClick={() => handleViewDetails(data.closedLeads, "Closed Leads", data.nameInTeam)}
                                  title="View details"
                                >
                                  <i className="bi bi-eye" />
                                </button>
                              </div>
                            </td>

                            <td>
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <span className="fw-semibold">{data.totalBooking}</span>
                                <button
                                  className="btn btn-light btn-sm"
                                  onClick={() => handleViewDetails(data.bookingLeads, "Booked Leads", data.nameInTeam)}
                                  title="View details"
                                >
                                  <i className="bi bi-eye" />
                                </button>
                              </div>
                            </td>

                            <td>
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <span className="fw-semibold">{data.totalRejected}</span>
                                <button
                                  className="btn btn-light btn-sm"
                                  onClick={() => handleViewDetails(data.rejectedLeads, "Rejected Leads", data.nameInTeam)}
                                  title="View details"
                                >
                                  <i className="bi bi-eye" />
                                </button>
                              </div>
                            </td>

                            <td>
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <span className="fw-semibold">{data.totalFollowUp}</span>
                                <button
                                  className="btn btn-light btn-sm"
                                  onClick={() => handleViewDetails(data.followUpLeads, "Leads Touched", data.nameInTeam)}
                                  title="View details"
                                >
                                  <i className="bi bi-eye" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">
                            No activity for selected range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination bottom */}
                <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                  <button
                    className="btn btn-light"
                    disabled={safePage === 1}
                    onClick={() => handlePageChange(safePage - 1)}
                  >
                    <i className="bi bi-caret-left" />
                  </button>

                  <span className="text-muted" style={{ fontSize: 13 }}>
                    Page <b>{safePage}</b> of <b>{totalPages}</b>
                  </span>

                  <button
                    className="btn btn-light"
                    disabled={safePage === totalPages}
                    onClick={() => handlePageChange(safePage + 1)}
                  >
                    <i className="bi bi-caret-right" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Details Modal */}
        {showModal && (
          <>
            <div className="modal-backdrop fade show" />

            <div className="modal fade show" tabIndex="-1" style={{ display: "block" }}>
              <div className="modal-dialog modal-fullscreen">
                <div className="modal-content ss-modal ss-modal-fullscreen px-3">
                  {/* Header */}
                  <div className="modal-header ss-modal-header">
                    <div>
                      <div className="modal-title fw-bold">{modalTitle}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Total rows: <b>{modalData.length}</b>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                      aria-label="Close"
                    />
                  </div>

                  {/* Body (NO SCROLL HERE) */}
                  <div className="modal-body p-0 ss-modal-body-no-scroll">
                    {modalData.length > 0 ? (
                      <div className="ss-modal-table-scroll" style={{fontSize: '0.9rem'}}>
                        <table className="table table-hover table-bordered text-center align-middle m-0 ss-sticky-table">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th className="text-start" style={{ maxwidth: 240 }}>Name</th>
                              <th className="text-start" style={{ minWidth: 160 }}>Phone</th>
                              <th style={{ width: 140 }}>Status</th>
                              <th style={{ minWidth: 220 }}>Activity Datetime</th>
                              <th style={{ minWidth: 220 }}>Last Update Lead</th>
                            </tr>
                          </thead>

                          <tbody>
                            {modalData.map((item, index) => (
                              <tr key={index}>
                                <td className="fw-semibold">{index + 1}</td>
                                <td className="text-start">{item.name || "-"}</td>
                                <td className="text-start">
                                  {(item.country || "") + (item.phone || "")}
                                </td>
                                <td>
                                  <span className="badge bg-light text-dark ss-badge">
                                    {item.status || "N/A"}
                                  </span>
                                </td>
                                <td>{formatDT(item.createdAt)}</td>
                                <td>{formatDT(item.updatedAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-3 text-muted">No data available.</div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="modal-footer">
                    <button className="btn btn-dark" onClick={() => setShowModal(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="mt-3" />
      </div>
    </div>
  );
}
